import { CheckCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import type { Handover } from '@entities/handover';

import styles from './Handover.module.css';

interface HandshakeCardProps {
  state: Handover;
}

/**
 * Код передачи и что с ним делать (FE-330, MVP_PRD §39.4, §39.5).
 *
 * Блок отвечает на вопрос, который возникает сразу после согласия: **как
 * автохаус меня найдёт и как я пойму, что это он**. Порядок ровно тот, в
 * котором всё произойдёт:
 *
 * 1. автохаус пишет на почту покупателя — адрес ушёл ему вместе с согласием;
 * 2. просит назвать этот код: так обе стороны понимают, что речь об одном деле;
 * 3. договаривается о встрече и присылает проект договора;
 * 4. после покупки присылает CarViQ.DE подтверждение с тем же кодом.
 *
 * Два правила блока:
 *
 * * **код — примета, а не пароль.** Он уходит открытым текстом в письме, и
 *   экран не называет его секретом: обещать защиту, которой нет, нельзя (§2);
 * * **подтверждение автохауса — его слова, а не вывод CarViQ.DE.** Оно приводится
 *   цитатой и **не заменяет отметку пользователя** (§39.3): «купил» отмечает
 *   тот, кто платит.
 *
 * Редизайн 2026-10 (DESIGN_SYSTEM §7.2): код — крупно, моноширинно, на синем
 * тинте. Кнопки копирования нет (решение заказчика от 22.10.2026).
 */
export function HandshakeCard({ state }: HandshakeCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  if (state.handover_code === null) return null;

  return (
    <section className={styles.section} aria-labelledby="handover-code">
      <h2 id="handover-code" className={styles.title}>
        {t('handover.code.title')}
      </h2>
      <p className={styles.subtitle}>{t('handover.code.subtitle')}</p>

      <p className={styles.codeValue}>{state.handover_code}</p>

      <ol className={styles.memoList}>
        <li>{t('handover.code.step1')}</li>
        <li>{t('handover.code.step2')}</li>
        <li>{t('handover.code.step3')}</li>
        <li>{t('handover.code.step4')}</li>
      </ol>
      <p className={styles.note}>{t('handover.code.notASecret')}</p>

      {state.dealer_confirmed_at === null ? null : (
        <div className={styles.confirmedBox}>
          <p className={styles.confirmTitle}>
            <CheckCircleIcon
              size={18}
              weight="fill"
              aria-hidden="true"
              className={styles.confirmedIcon}
            />
            <span>
              {t('handover.code.dealerConfirmed', {
                date: new Date(state.dealer_confirmed_at).toLocaleString(locale),
              })}
            </span>
          </p>
          {state.dealer_confirmation_quote === null ? null : (
            <blockquote className={styles.quote}>«{state.dealer_confirmation_quote}»</blockquote>
          )}
          <p className={styles.note}>{t('handover.code.dealerConfirmedNote')}</p>
        </div>
      )}
    </section>
  );
}
